/**
 * Zone Features API Service
 *
 * Feature templates are reusable “capability packs”.
 * Assignments apply a template to a specific zone.
 */

import { api } from './api';

export interface FeatureTemplate {
  id: string;
  template_key: string;
  name: string;
  category: string;
  description?: string;
  version: string;
  default_parameters: Record<string, any>;
  ruleset_spec: Record<string, any>;
  is_active: boolean;
}

export interface ZoneFeatureAssignment {
  id: string;
  site_id: string;
  zone_db_id: string;
  zone_id: string;
  zone_name?: string;
  camera_id: string;
  device_id?: string;
  enabled: boolean;
  priority: number;
  parameter_overrides: Record<string, any>;

  feature_template_id: string;
  feature_template_version: string;
  template_key: string;
  template_name: string;
  template_category: string;
  template_version: string;
  default_parameters: Record<string, any>;
  effective_parameters?: Record<string, any>;
}

export const zoneFeaturesApi = {
  listTemplates: async (activeOnly: boolean = true): Promise<{ templates: FeatureTemplate[]; count: number }> => {
    const resp = await api.get('/api/v1/feature_templates', {
      params: { active_only: activeOnly },
    });
    return resp.data;
  },

  listZoneAssignments: async (zoneDbId: string): Promise<{ assignments: ZoneFeatureAssignment[]; count: number }> => {
    const resp = await api.get(`/api/v1/zones/${encodeURIComponent(zoneDbId)}/features`);
    return resp.data;
  },

  createAssignment: async (
    zoneDbId: string,
    payload: {
      feature_template_id: string;
      feature_template_version?: string;
      enabled?: boolean;
      priority?: number;
      parameter_overrides?: Record<string, any>;
    },
  ): Promise<{ ok: boolean; assignment_id: string }> => {
    const resp = await api.post(`/api/v1/zones/${encodeURIComponent(zoneDbId)}/features`, payload);
    return resp.data;
  },

  updateAssignment: async (
    assignmentId: string,
    payload: Partial<{ enabled: boolean; priority: number; parameter_overrides: Record<string, any> }>,
  ): Promise<{ ok: boolean; assignment_id: string }> => {
    const resp = await api.patch(`/api/v1/zone_feature_assignments/${encodeURIComponent(assignmentId)}`, payload);
    return resp.data;
  },

  deleteAssignment: async (assignmentId: string): Promise<{ ok: boolean }> => {
    const resp = await api.delete(`/api/v1/zone_feature_assignments/${encodeURIComponent(assignmentId)}`);
    return resp.data;
  },
};


